import { useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { addCommentToJob, deleteCommentFromJob } from "../../apis/JobApis";
import { useAuth } from "../Authentication/AuthContext";
import { useToastHelpers } from "../Toasts/ToastUtils";

export default function CommentSection({ job }) {
    const { user, loggedInUser } = useAuth();
    const [comments, setComments] = useState(
        (job?.comments || []).slice().reverse()
    );
    const [text, setText] = useState("");
    const { errorToast, successToast } = useToastHelpers();

    const handleAddComment = async () => {
        if (!text.trim()) return;
        const newComment = {
            userId: loggedInUser.id,
            userName:
                loggedInUser.firstName + " " + loggedInUser.lastName ||
                loggedInUser.email,
            text,
            createdAt: new Date().toISOString(),
        };
        await addCommentToJob(job.id, newComment, user.token)
            .then(() => {
                setComments([newComment, ...comments]);
                setText("");
                successToast("Comment added successfully.");
            })
            .catch((err) => {
                errorToast("Failed to add comment. Please try again.");
                return;
            });
    };

    const handleDeleteComment = async (id) => {
        deleteCommentFromJob(job.id, id, user.token)
            .then(() => {
                successToast("Comment deleted successfully.");
                setComments(comments.filter((c) => c.id !== id));
            })
            .catch((err) => {
                errorToast("Failed to delete comment. Please try again.");
            });
    };

    return (
        <div className="mt-10 ca-card p-6">
            <h2 className="text-lg font-semibold mb-3">💬 Comments</h2>

            {/* Input */}
            <div className="flex gap-2 mb-4">
                <input
                    type="text"
                    disabled={!loggedInUser}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Write a comment..."
                    className="ca-input flex-1 text-sm"
                />
                <button
                    onClick={handleAddComment}
                    className="ca-btn-primary px-4 py-2 text-sm"
                >
                    Post
                </button>
            </div>

            {loggedInUser ? null : (
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                    Please log in to post comments.
                </p>
            )}

            {/* Comments List */}
            {comments.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-sm">No comments yet. Be the first!</p>
            ) : (
                <ul className="space-y-4">
                    {comments.map((c) => (
                        <li key={c.id} className="bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm p-4 rounded-xl shadow-sm ring-1 ring-black/5 dark:ring-white/5">
                            <div className="flex justify-between items-center">
                                <span className="font-medium text-sm dark:text-white">{c.userName}</span>
                                <span className="text-xs text-gray-400 dark:text-gray-500">
                                    {formatDistanceToNow(new Date(c.createdAt), {
                                        addSuffix: true,
                                    })}
                                </span>
                            </div>
                            <p className="text-gray-800 dark:text-gray-200 text-sm mt-1">{c.text}</p>

                            {/* Delete button for owner */}
                            {c.userId === loggedInUser?.id && (
                                <button
                                    onClick={() => handleDeleteComment(c.id)}
                                    className="text-xs text-red-500 dark:text-red-400 mt-2 hover:underline"
                                >
                                    Delete
                                </button>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
